import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';

import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-merge-modal',
  standalone: true,
  imports: [FormsModule],
  template: `
    @if (isOpen) {
      <div class="modal-overlay">
        <div class="modal-container">
          <!-- Header -->
          <div class="modal-header">
            <div class="header-content">
              <div class="header-icon">
                <i class="material-symbols-rounded">merge</i>
              </div>
              <div>
                <h3 class="header-title">Merge {{ type === 'entity' ? 'Entities' : type === 'meter' ? 'Meters' : 'Addresses' }}</h3>
                <p class="header-subtitle">
                  Select the primary record. Others will be merged into it.
                  @if (subtitleNote) {
                    {{ subtitleNote }}
                  }
                </p>
              </div>
            </div>
          </div>
          <!-- Body -->
          <div class="modal-body">
            @for (id of candidates; track id) {
              <div
                (click)="selectedId = id"
                class="candidate-item"
                [class.selected]="selectedId === id"
                >
                <!-- Radio Indicator -->
                <div class="radio-indicator" [class.selected]="selectedId === id">
                  @if (selectedId === id) {
                    <div class="radio-dot"></div>
                  }
                </div>
                <!-- Label -->
                <div class="candidate-label">
                  <span class="label-text" [title]="getLabel(id)">{{ getLabel(id) }}</span>
                  @if (getSubLabel(id); as sub) {
                    <span class="sublabel-text">{{ sub }}</span>
                  }
                </div>
              </div>
            }
          </div>
          <!-- Footer -->
          <div class="modal-footer">
            <button (click)="onCancel()" class="btn-cancel">Cancel</button>
            <button (click)="onConfirm()" [disabled]="!selectedId" class="btn-confirm">Merge Items</button>
          </div>
        </div>
      </div>
    }
    `,
  styleUrl: './merge-modal.component.scss'
})
export class MergeModalComponent implements OnChanges {
    @Input() isOpen = false;
    @Input() type: 'entity' | 'address' | 'meter' | null = null;
    @Input() candidates: string[] = [];
    @Input() labelMap: Record<string, string> = {};
    /** Small hierarchy line rendered under the main label so the user can
     *  tell which parent context each candidate belongs to (e.g. the LEN
     *  name for an SA, or "{LEN} → {SA}" for a meter). */
    @Input() subLabelMap: Record<string, string> = {};
    /** Extra sentence appended to the subtitle (e.g. what happens to the
     *  candidates' billing addresses on a LEN merge). */
    @Input() subtitleNote = '';

    @Output() closeModal = new EventEmitter<void>();
    @Output() confirm = new EventEmitter<string>();

    selectedId = '';

    ngOnChanges(changes: SimpleChanges) {
        if (changes['isOpen'] || changes['candidates']) {
            if (this.isOpen && this.candidates.length > 0) {
                this.selectedId = this.candidates[0];
            }
        }
    }

    getLabel(id: string): string {
        if (this.labelMap[id]) return this.labelMap[id];
        if (this.type === 'entity') return id;
        // For addresses: format is "entity::street::city"
        const parts = id.split('::');
        return `${parts[1]}, ${parts[2]}`;
    }

    getSubLabel(id: string): string {
        if (this.subLabelMap[id]) return this.subLabelMap[id];
        if (this.type !== 'address') return '';
        // Legacy "entity::street::city" format — only return the entity
        // segment when the id actually contains the separator. Raw
        // groupIds like "sa-2" must not leak into the sublabel.
        const parts = id.split('::');
        return parts.length > 1 ? parts[0] : '';
    }

    onCancel() {
        this.closeModal.emit();
    }

    onConfirm() {
        if (this.selectedId) {
            this.confirm.emit(this.selectedId);
        }
    }
}
